import type { components } from '@/api/schema'
import { EXTREME_GREED_FROM, moodBand } from '@/features/market/lib/mood'
import { formatBsDate } from '@/lib/bsDate'
import { div, money, toChartNumber } from '@/lib/money'
import { sentenceCase } from '@/lib/text'
import type { CandlePoint, IndexPoint, MarketBreadth, MarketMood } from '@/schemas/market'

type Wire<K extends keyof components['schemas']> = components['schemas'][K]

/**
 * `null` means mm-be did not report a figure for this session — never
 * silently rescaled to "Rs 0 Cr", which would read as a real, tiny turnover.
 */
export const rupeesToCrore = (value: string | null): string | null =>
  value === null ? null : div(money(value), 10_000_000)

export const rupeesToArba = (value: string | null): string | null =>
  value === null ? null : div(money(value), 1_000_000_000)

export const kittaToCrore = (value: number | null): string | null =>
  value === null ? null : div(money(value), 10_000_000)

export const breadthFromWire = (breadth: Wire<'Breadth'>): MarketBreadth => ({
  rose: breadth.advances,
  fell: breadth.declines,
  flat: breadth.unchanged,
})

export const chartValues = (values: readonly string[]): number[] =>
  values.map((value) => toChartNumber(money(value)))

/**
 * A row's five-day sparkline as chart numbers: all of its points, or `[]` when
 * any one is not a decimal.
 *
 * `chartValues` throws on a bad point, which is right for a series a chart is
 * the whole point of, and wrong for a sparkline: the row's price, change and
 * name are still good, and a throw here fails every row of the page the one bad
 * series sits on. `[]` is a state `Sparkline` already draws as nothing (it needs
 * two points), and mm-be sends it for a scrip with no price yet. The whole
 * series goes, not just the bad point — a line with a session missing is a
 * different shape from the one mm-be sent.
 *
 * Any list that draws a sparkline reads it through here; a real chart series
 * (index history, bars) must keep failing loudly, so it does not.
 */
export const sparklineValues = (points: readonly string[]): number[] => {
  try {
    return chartValues(points)
  } catch (error) {
    console.warn('drew no sparkline for a malformed series', error, points)
    return []
  }
}

/**
 * A series stamp in unix seconds.
 *
 * `IndexPoint.t` is a bare date ("2026-09-16") today, which is read at UTC
 * midnight — not local midnight: `new Date('2026-09-16')` and
 * `new Date('2026-09-16T00:00:00')` are different instants, and the second
 * would shift every bar by the reader's own offset. A stamp that already
 * carries a time is parsed as-is, so a later intraday feed needs no change
 * here.
 */
const pointTime = (t: string): number =>
  Math.floor(new Date(t.includes('T') ? t : `${t}T00:00:00Z`).getTime() / 1000)

const chartNumber = (value: string | number | null | undefined): number | undefined => {
  if (value === null || value === undefined) return undefined
  const raw = typeof value === 'number' ? String(value) : value.trim()
  // `money` throws on anything that is not a decimal, and a malformed bar is
  // not an exception worth taking the whole Overview down for — it is a series
  // that cannot be drawn as candles, which the caller below reports as such.
  if (raw === '' || !Number.isFinite(Number(raw))) return undefined
  return toChartNumber(money(raw))
}

/**
 * `stock_price` stores a price it never recorded as 0, and an index is never
 * 0. `/charts/history/` drops such a session rather than drawing it
 * (mm-be services/chart/bars.py `_ohlc`); the history series below does the
 * same, so a gap in the record is not drawn as a crash to zero.
 */
const isRecorded = (price: number): boolean => price > 0

interface Session {
  open: number
  high: number
  low: number
  close: number
}

/**
 * The session a history point carries beside its `value`.
 *
 * mm-be's `bar_to_point` (services/market/indices.py) sends `open`, `high`,
 * `low` and `close` on every `IndexPoint`, from the same adjusted
 * `stock_price` columns `/charts/history/` reads. The schema calls them
 * required, but a deployment older than that change sends none, and
 * `chartNumber` reads a missing field as `undefined` — which the callers below
 * turn into "no candles" rather than a crash.
 */
const sessionOf = (point: Wire<'IndexPoint'>): Session | undefined => {
  const open = chartNumber(point.open)
  const high = chartNumber(point.high)
  const low = chartNumber(point.low)
  const close = chartNumber(point.close)
  if (open === undefined || high === undefined || low === undefined || close === undefined) {
    return undefined
  }
  return { open, high, low, close }
}

/**
 * The line: one level per session, oldest first.
 *
 * The session's CLOSE where the point carries one, not `value`. `value` is
 * `stock_price.ltp`, which the older rows never recorded: on dev every NEPSE
 * session from 2000 to 2011 sends `value: "0.00"` beside a real close, so ALL
 * drew twelve years at zero. The two agree on every row that has both, and
 * mm-be falls the close back to the ltp for a live session that has no close
 * yet — so reading the close changes nothing else. `value` is still what a
 * build without sessions is drawn from.
 */
export const chartPoints = (points: readonly Wire<'IndexPoint'>[]): IndexPoint[] =>
  points.flatMap((point) => {
    const level = sessionOf(point)?.close ?? chartNumber(point.value)
    return level === undefined || !isRecorded(level)
      ? []
      : [{ time: pointTime(point.t), value: level }]
  })

/**
 * A session's minute points (`range=1D`) as the line's coordinates: the same stamp reading and the
 * same "never recorded" rule as `chartPoints`, over the bare value.
 */
export const intradayPoints = (points: readonly { t: string; value: string }[]): IndexPoint[] =>
  points.flatMap((point) => {
    const level = chartNumber(point.value)
    const time = pointTime(point.t)
    return level === undefined || !isRecorded(level) || !Number.isFinite(time)
      ? []
      : [{ time, value: level }]
  })

/**
 * mm-be's score as a 0–100 coordinate. A value that is not a number stays NaN: the mood schema
 * refuses it, so it is never drawn as the fear end of the gauge.
 */
const moodScore = (value: string): number => Math.min(100, Math.max(0, Number(value)))

/**
 * Today's `fear_greed` as the market mood (`features/market/lib/mood.ts`), or null when mm-be has
 * no reading for the latest session.
 */
export const moodFromWire = (
  reading: Pick<Wire<'FearGreed'>, 'date' | 'score' | 'label'> | null,
): MarketMood | null => {
  if (reading === null) return null
  const score = moodScore(reading.score)
  if (Number.isNaN(score)) return null
  const band = moodBand(reading.label, score).key
  return {
    score,
    band,
    extreme: band === 'greed' && score >= EXTREME_GREED_FROM,
    date: reading.date,
  }
}

/** One session of `/market/fear-greed/` as a trend bar. */
export const moodDayFromWire = (row: Wire<'FearGreedDay'>) => {
  const score = moodScore(row.score)
  return { date: row.date, score, band: moodBand(row.label, score).key }
}

/**
 * The same history as candles — the Candles view of the series `chartPoints`
 * draws as a line, so toggling between the two never changes the window.
 *
 * All four prices on EVERY point, or no candles at all, the rule `barCandles`
 * keeps too: a history carrying sessions for some dates and a bare value for
 * others is a build mid-migration, and drawing only the dates that did arrive
 * would shorten the series under a chip promising a range. Within a history
 * that does carry them, a session with a price never recorded is left out, as
 * `/charts/history/` leaves it out.
 *
 * `undefined` rather than `[]` for nothing to draw, so "no candles" stays one
 * state — see `resolveChartView`. No `volume`: each point carries one now, but
 * the index chart draws its candles without a histogram.
 */
export const chartCandles = (points: readonly Wire<'IndexPoint'>[]): CandlePoint[] | undefined => {
  const candles: CandlePoint[] = []
  for (const point of points) {
    const session = sessionOf(point)
    if (session === undefined) return undefined
    if (!Object.values(session).every(isRecorded)) continue
    candles.push({ time: pointTime(point.t), ...session })
  }
  return candles.length === 0 ? undefined : candles
}

/**
 * A `/charts/history/` BarSeries as chart bars, or `undefined` when there are
 * none to draw.
 *
 * The payload is COLUMN-oriented — six parallel arrays plus `next_time` —
 * because that is the wire format the TradingView UDF datafeed this endpoint
 * ports uses. It is zipped back into one object per bar here, once, so that
 * nothing downstream has to reason about six arrays staying in step.
 *
 * `undefined` rather than `[]` for an empty series, so "there are no candles"
 * is one state and not two — the same rule `candlePointSchema` and
 * `resolveChartView` follow.
 *
 * ── The empty window, and why `next_time` is not read ────────────────────────
 *
 * `BarSeries.next_time` is documented as "the `t` of the latest earlier bar,
 * set only when the window is empty", which sounds like the thing to follow
 * when `t` comes back empty. It is not reachable from the requests this app
 * makes. mm-be computes it under `if not t and from_date is not None`
 * (`services/chart/bars.py`), and sets `from_date = None if countback or
 * from_time is None else …` — so a request that carries `countback`, or no
 * window at all, can NEVER produce a non-null `next_time`. Both of the shapes
 * `barWindow.ts` sends are exactly those two.
 *
 * So an empty `t` here means one thing only: this symbol has no drawable bar
 * at all — every session either missing an O/H/L/C or carrying a zero, which
 * mm-be drops rather than zero-fills. There is nowhere earlier to jump to, and
 * `undefined` is the honest answer. Reading `next_time` and rendering a "jump
 * back" affordance off it would be a branch no response can enter.
 *
 * ── Volume ───────────────────────────────────────────────────────────────────
 *
 * `v` is nullable PER BAR and means different things per symbol type: share
 * volume for a stock, rupee turnover for an index (mm-be sends `null` for an
 * index with no turnover recorded). A null becomes an ABSENT `volume`, never
 * `0` — `PriceChart` draws no histogram bar for an absent volume and a
 * zero-height one for a real zero, and "nobody reported it" must not be drawn
 * as "nothing traded".
 */
export const barCandles = (series: Wire<'BarSeries'>): CandlePoint[] | undefined => {
  const bars: CandlePoint[] = []

  for (const [index, time] of series.t.entries()) {
    const open = chartNumber(series.o[index])
    const high = chartNumber(series.h[index])
    const low = chartNumber(series.l[index])
    const close = chartNumber(series.c[index])
    // All four, on EVERY bar, or nothing: a series with whole sessions for
    // some dates and a bare close for others cannot be drawn as candles
    // without inventing three quarters of the rest, and inventing them is what
    // the inert "Candles" chip was replaced for. mm-be already drops any bar it
    // cannot build honestly, so a gap here is a contract break, not a holiday.
    if (open === undefined || high === undefined || low === undefined || close === undefined) {
      return undefined
    }
    if (!Number.isFinite(time)) return undefined

    const volume = chartNumber(series.v[index])
    bars.push({ time, open, high, low, close, ...(volume === undefined ? {} : { volume }) })
  }

  return bars.length === 0 ? undefined : bars
}

export const displayDate = formatBsDate

export const displayTime = (iso: string | null): string => {
  if (iso === null) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kathmandu',
  })
}

export const displayTimeWithSeconds = (iso: string | null): string => {
  if (iso === null) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kathmandu',
  })
}

export const pageTotal = (meta: Record<string, unknown> | null, fallback: number): number => {
  const total = meta?.['total'] ?? meta?.['count']
  return typeof total === 'number' && Number.isInteger(total) && total >= 0 ? total : fallback
}

export const marketStateIsOpen = (state: string): boolean => state.toLowerCase() === 'open'

export const sectorSlug = (key: string): string => key.toLowerCase()

/**
 * The value mm-be filters sectors on, from the kebab id the UI carries.
 *
 * ── Why this is now a round trip and not a table ─────────────────────────────
 *
 * Every sector id in this app is built by `sectorSlug` from the API's OWN
 * sector key — `CompanySector.symbol`, handed over on each heatmap tile
 * (`services/market/heatmap.py:20`) and on every `/market/sectors/` row. So the
 * inverse of `sectorSlug` is the only correct answer, and it is exact for
 * whatever keys a given deployment actually holds. mm-be matches with
 * `company__sector__symbol__iexact=` (`services/market/repository.py:152,336`),
 * so the case is tolerated either way; upper-case is used because that is how
 * the key is spelled everywhere else.
 *
 * This used to translate through a hardcoded `{slug: KEY}` table, and that is
 * exactly how tapping Hydropower on the heatmap came to request
 * `heatmap/?sector=HYDRO`: the tile's own id `hydropower` — lower-cased from
 * the real key `HYDROPOWER` — was rewritten to `HYDRO`, the abbreviation
 * mm-be's DEV SEED uses (`bootstrap_dev.py:97` creates `("HYDRO",
 * "Hydropower")`). The request 200s and comes back empty, because the word is
 * wrong. Flipping the literal to `HYDROPOWER` would only move the bug to the
 * seeded database; the table cannot be right for both, and it does not need to
 * be — the key that came back with the data is right for both.
 *
 * No alias is kept for the name-derived spellings the table also carried
 * ("commercial-banks", "manufacturing-and-processing"). Nothing in this app has
 * ever produced one: the ids come from keys, not from display names. An alias
 * would be a guess about a deployment's data, made in the one place a wrong
 * guess returns an empty screen with a 200.
 */
export const backendSectorKey = (slug: string): string => slug.toUpperCase()

/**
 * The short label a card prints for an index or sector: the API name with a
 * trailing "Sub-Index" dropped ("Life Insurance Sub-Index" → "Life Insurance").
 *
 * Only the suffix goes. " Index" on its own stays — "Float Index" without it
 * is just "Float" — and nothing is abbreviated: a shorter name the API never
 * sent would be a name this app made up.
 */
export const shortMarketLabel = (name: string): string => {
  const trimmed = name.replace(/\s*sub[\s-]?index$/i, '').trim()
  return trimmed === '' ? name : trimmed
}

/**
 * The mock's own names where they are not simply the API's in sentence case: it shortens the long
 * ones and pluralises the kind ("Development banks"). Keyed by the name with its suffix dropped and
 * lower-cased.
 */
const SHORT_INDEX_NAME: Readonly<Record<string, string>> = {
  'manufacturing and processing': 'Manufacturing',
  'development bank': 'Development banks',
  'non life insurance': 'Non-life insurance',
}

/**
 * An index's display name, in one place for every surface that prints it (the Indices table, the
 * chart headline above it, Overview's Indices panel). mm-be sends them in mixed casing and with
 * its own suffixes ("NEPAL STOCK EXCHANGE", "Sensitive Float Index", "Float index", "Hotels And
 * Tourism Sub-Index"); the mock prints "NEPSE", "Sensitive float", "Float", "Hotels and tourism".
 *
 * NEPSE is the one reading named by its key, because its API name is the long form. Elsewhere the
 * trailing "Sub-Index" / "Index" goes, a shouted name is lowered first, and the rest is sentence
 * case (an acronym inside a mixed-case name keeps its capitals).
 */
export const indexDisplayName = (name: string, key: string): string => {
  if (key.toUpperCase() === 'NEPSE') return 'NEPSE'
  const bare = name.replace(/\s*(sub[\s-]?)?index$/i, '').trim()
  if (bare === '') return sentenceCase(name)
  const short = SHORT_INDEX_NAME[bare.toLowerCase()]
  if (short !== undefined) return short
  return sentenceCase(bare === bare.toUpperCase() ? bare.toLowerCase() : bare)
}
